import { createDirectus, rest, readItem, readItems, createItem,
  createItems, updateItem, updateItems, deleteItem, deleteItems,
  authentication, passwordRequest, passwordReset,
  inviteUser, acceptUserInvite, readMe, updateMe, 
  readUsers, createUser, updateUser, deleteUser,
  generateTwoFactorSecret, enableTwoFactor, disableTwoFactor,
  deleteFile, updateFile, uploadFiles, aggregate,
  
} from '@directus/sdk';
import { helper } from '../lib/helper';
import { apiStorage } from './apiStorage';

const directusClient = createDirectus(process.env.API_URL as string).with(rest()).with(authentication('json', {autoRefresh: false, storage: apiStorage()}));

export function syncLog(logs : any[]) {
  return directusClient.request(createItems('app_log', logs));
}

export function authenticate(email : string, password : string, otp? : string) {
  return directusClient.login(email, password, {otp: otp});
}

export function refresh() {
  return directusClient.refresh()
}

export function logout() {
  return directusClient.logout();
}

export function appSettingGet(lang : string) {
  let url = helper.stringHasValue(lang) ? `system?app=${process.env.APP_ID}&lang=${lang}` : `system?app=${process.env.APP_ID}`;
       
  return Promise.all([
    directusClient.request(() => ({
      path: url,
      method: 'GET',
    })),
    directusClient.request(() => ({
      path: `auth`,
      method: 'GET',
    }))
  ]);
}

export function webPushReg(regInfo : any) {
  return directusClient.request(() => ({
    path: `/webpush/reg`,
    body: JSON.stringify(regInfo),
    method: 'POST',
  }));
}

export function userPasswordResetRequest(email : string, url : string) {
  return directusClient.request(passwordRequest(email, url));
}

export function userPasswordReset(token : string, newPwd : string) {
  return directusClient.request(passwordReset(token, newPwd));
}

export function userInvite(email : string, role : string, inviteURL : string) {
  return directusClient.request(inviteUser(email, role, inviteURL));
}

export function userAcceptInvite(token : string, password : string) {
  return directusClient.request(acceptUserInvite(token, password));
}

// export function userProfile() {
//   return directusClient.request(readMe());
// }

export function userProfile() {
  const getExtendedProfile = async () => {
    let profile = await directusClient.request(readMe());

    let extendedProfile = await directusClient.request(readItems('landlord', {
      filter: {"user": { "_eq": profile.id }}
    }));

    if(helper.arrayHasItem(extendedProfile)) {
      profile.landlord = extendedProfile[0].id;
      profile.mobile = extendedProfile[0].mobile;
      profile.gender = extendedProfile[0].gender;
      profile.address = extendedProfile[0].address;
    }

    return profile;
  }

  return getExtendedProfile();
}

export function userProfileUpdate(data : any) {
  let profile = helper.deepCopy(data);
  delete profile.landlord;
  delete profile.gender;
  delete profile.mobile;
  delete profile.address;
  delete profile.address_unit;
  delete profile.address_street;
  delete profile.address_country;
  delete profile.address_building;
  delete profile.address_house_no;
  delete profile.address_postal_code;

  const setExtendedProfile = async () => {
    let updatedProfile = await directusClient.request(updateMe(profile));

    if(helper.stringHasValue(data.landlord)) {
      await directusClient.request(updateItem('landlord', data.landlord, {
        gender: data.gender,
        mobile: data.mobile,
        address: {
          unit: data.address_unit,
          street: data.address_street,
          country: data.address_country,
          building: data.address_building,
          house_no: data.address_house_no,
          postal_code: data.address_postal_code
        }
      }));
    }
    else {
      await directusClient.request(createItem('landlord', {
        user: updatedProfile.id,
        gender: data.gender,
        mobile: data.mobile,
        address: {
          unit: data.address_unit,
          street: data.address_street,
          country: data.address_country,
          building: data.address_building,
          house_no: data.address_house_no,
          postal_code: data.address_postal_code
        }
      }));
    }

    return updatedProfile;
  }

  return setExtendedProfile();
}

export function userUIProfileUpdate(data : any) {
  return directusClient.request(updateMe(data));
}

export function tfaGenerate(currentPwd : string) {
  return directusClient.request(generateTwoFactorSecret(currentPwd));
}

export function tfaEnable(secret : string, otp : string) {
  return directusClient.request(enableTwoFactor(secret, otp));
}

export function tfaDisable(otp : string) {
  return directusClient.request(disableTwoFactor(otp));
}

export function fileNew(file : any) {
  const form = new FormData();
  form.append("file", file);
  return directusClient.request(uploadFiles(form));
}

export function fileDelete(fileId : string) {
  return directusClient.request(deleteFile(fileId));
}

export function loginPersistencyConfigSet(setting : any) {
  return directusClient.request(() => ({
    path: `/system/loginpersistencyconfig`,
    body: JSON.stringify(setting),
    method: 'POST',
  }));
}

export function loginSecurityConfigSet(setting : any) {
  return directusClient.request(() => ({
    path: `/system/loginsecurityconfig`,
    body: JSON.stringify(setting),
    method: 'POST',
  }));
}

export function userMgtConfigSet(setting : any) {
  return directusClient.request(() => ({
    path: `/system/usermgtconfig`,
    body: JSON.stringify(setting),
    method: 'POST',
  }));
}

export function loggingConfigSet(setting : any) {
  return directusClient.request(() => ({
    path: `/system/loggingconfig`,
    body: JSON.stringify(setting),
    method: 'POST',
  }));
}

export function codeGetList(query : any) {
  return Promise.all([
    directusClient.request(aggregate('app_code', {
      query: {
        filter: query.filter
      },
      aggregate: {
        count: '*',
      },
      // groupBy: ['type'],
    })),
    directusClient.request(readItems('app_code', query))
  ]);
}

export function codeSet(data : any) {
  const newData : any = {
    category: data.category,
    code: data.code,
    text: data.text_en_US,
    sort: data.sort,
    status: data.status
  }
  if(!helper.stringHasValue(data.id)) // Only for create code
    newData.application = data.application;

  if(!helper.stringHasValue(data.id))
    return directusClient.request(createItem('app_code', newData));
  else
    return directusClient.request(updateItem('app_code', data.id, newData));
}

export function codeTranslationSet(data : any, lang : string) {
  let codeTransId = 0;
  const newData : any = {
    languages_code: lang,
    text: data[`text_${lang.replace('-', '_')}`]
  }

  // Get the translation id
  if(helper.arrayHasItem(data.translations)) {
    let transItem = data.translations.find((item : any) => item.languages_code === lang);
    if(helper.isNotNullAndUndefined(transItem)) {
      codeTransId = transItem.id;
    }
  }

  // If it is a new record, include the app_code_id field
  if(codeTransId <= 0) {
    newData.app_code_id = data.id;
  }
  
  // console.log('codeTranslationSet: ', newData);
  if(codeTransId <= 0)
    return directusClient.request(createItem('app_code_translations', newData));
  else
    return directusClient.request(updateItem('app_code_translations', codeTransId, newData));
}

export function codeDelete(id : any) {
  return directusClient.request(deleteItem('app_code', id));
}

export function codeTranslationDelete(ids : any[]) {
  return directusClient.request(deleteItems('app_code_translations', ids));
}

export function roleSet(role : any) {
  return directusClient.request(() => ({
    path: `/system/rolesetting`,
    body: JSON.stringify(role),
    method: 'POST',
  }));
}

export function menuGetList() {
  const query = {
    fields: ['id', 'status', 'sort', 'name', 'tooltip', 'route', 'param', 'icon', 'is_container',
    'is_public', 'parent_menu', 'date_effective', 'date_expired', 'role.id', 'role.directus_roles_id',
    'translations.id', 'translations.languages_code', 'translations.name', 'translations.tooltip'],
    filter: {"application": { "_eq": process.env.APP_ID }},
    limit: -1
  };

  return directusClient.request(readItems('app_menu', query));
}

export function menuSet(data : any) {
  return directusClient.request(() => ({
    path: `/system/menu`,
    body: JSON.stringify(data),
    method: 'POST',
  }));
}

export function menuDelete(menuId : string) {
  return directusClient.request(() => ({
    path: `/system/menu/${menuId}`,
    method: 'DELETE',
  }));
}

export function screenGetList(query : any) {
  return Promise.all([
    directusClient.request(aggregate('app_screen', {
      query: {
        filter: query.filter
      },
      aggregate: {
        count: '*',
      },
      // groupBy: ['type'],
    })),
    directusClient.request(readItems('app_screen', query))
  ]);
}

export function screenSet(data : any) {
  return directusClient.request(() => ({
    path: `/system/screen`,
    body: JSON.stringify(data),
    method: 'POST',
  }));
}

export function screenDelete(screenId : string) {
  return directusClient.request(() => ({
    path: `/system/screen/${screenId}`,
    method: 'DELETE',
  }));
}

export function userGetList(query : any) {
  return Promise.all([
    directusClient.request(aggregate('directus_users', {
      query: {
        filter: query.filter
      },
      aggregate: {
        count: '*',
      },
      // groupBy: ['type'],
    })),
    directusClient.request(readUsers(query))
  ]);
}

export function userCreate(data : any) {
  return directusClient.request(createUser(data));
}

export function userUpdate(data : any) {
  let id = data.id;
  let userDetail = helper.deepCopy(data);
  // Remove the fields that not allow to change.
  delete userDetail.id;
  delete userDetail.password;
  delete userDetail.auth_data;
  delete userDetail.external_identifier;
  delete userDetail.last_access;
  delete userDetail.last_page;
  delete userDetail.provider;
  delete userDetail.tfa_secret;
  delete userDetail.token;
  delete userDetail.newpassword;
  delete userDetail.confirmpassword;

  return directusClient.request(updateUser(id, userDetail));
}

export function userDelete(id : string) {
  return directusClient.request(deleteUser(id));
}

export function userExist(email : any) {
  return directusClient.request(() => ({
    path: `/auth/userexist/${email}`,
    method: 'GET',
  }));
}

export function userDisableTFA(id : string) {
  return directusClient.request(() => ({
    path: `users/${id}/tfa/disable`,
    body: JSON.stringify({}),
    method: 'POST',
  }));
}

export function customerGetList(query : any) {
  // console.log('Api customerGetList', query);
  return Promise.all([
    directusClient.request(aggregate('customer', {
      query: {
        filter: query.filter
      },
      aggregate: {
        count: '*',
      },
      // groupBy: ['type'],
    })),
    directusClient.request(readItems('customer', query))
  ]);
}

export function customerGetInfo(customerId : string) {
  return directusClient.request(readItem('customer', customerId));
}

export function customerCreate(data : any) {
  return directusClient.request(() => ({
    path: `/customer`,
    method: 'POST',
    body: JSON.stringify(data)
  }));
  
}

export function customerUpdate(data : any) {
  let id = data.id;
  let custDetail = helper.deepCopy(data);
  // Remove the fields that not allow to change.
  delete custDetail.id;
  delete custDetail.devotee_serial;
  delete custDetail.member_serial;

  return directusClient.request(() => ({
    path: `/customer/${id}`,
    method: 'PATCH',
    body: JSON.stringify(data)
  }));
}

export function customerWithdraw(id : string, reason : string) {
  return directusClient.request(() => ({
    path: `/customer/withdraw/${id}`,
    method: 'PATCH',
    body: JSON.stringify({reason})
  }));
}

export function customerTerminate(id : string, reason : string) {
  return directusClient.request(() => ({
    path: `/customer/terminate/${id}`,
    method: 'PATCH',
    body: JSON.stringify({reason})
  }));
}

export function customerDisable(id : string, reason : string) {
  return directusClient.request(() => ({
    path: `/customer/disable/${id}`,
    method: 'PATCH',
    body: JSON.stringify({reason})
  }));
}

export function customerBlacklist(id : string, reason : string) {
  return directusClient.request(() => ({
    path: `/customer/blacklist/${id}`,
    method: 'PATCH',
    body: JSON.stringify({reason})
  }));
}

export function customerReactivate(id : string, reason : string) {
  return directusClient.request(() => ({
    path: `/customer/reactivate/${id}`,
    method: 'PATCH',
    body: JSON.stringify({reason})
  }));
}

export function customerApproveMemApp(id : string) {
  return directusClient.request(() => ({
    path: `/customer/approve/${id}`,
    method: 'PATCH'
  }));
}

export function customerRejectMemApp(id : string) {
  return directusClient.request(() => ({
    path: `/customer/reject/${id}`,
    method: 'PATCH'
  }));
}

export function customerDelete(id : string) {
  return directusClient.request(() => ({
    path: `/customer/${id}`,
    method: 'DELETE'
  }));
}

export function customerQuery(query : any) {
  return directusClient.request(readItems('customer', query));
}

// export function transactionGetList(query : any) {
//   // console.log('Api customerGetList', query);
//   return Promise.all([
//     directusClient.request(aggregate('transaction', {
//       query: {
//         filter: query.filter
//       },
//       aggregate: {
//         count: '*',
//       },
//       // groupBy: ['type'],
//     })),
//     directusClient.request(readItems('transaction', query))
//   ]);
// }

// export function transactionDonationBoxGetList(query : any) {
//   return directusClient.request(() => ({
//     path: `/transaction/donationbox/trans?filter=${helper.isNotNullAndUndefined(query.filter) ? JSON.stringify(query.filter) : ''}&sort=${query.sort}&limit=${query.limit}&skip=${query.offset}`,
//     method: 'GET',
//   }));
// }

// export function transactionCounterReceiptGetList(query : any) {
//   return directusClient.request(() => ({
//     path: `/transaction/counterreceipt/trans?filter=${helper.isNotNullAndUndefined(query.filter) ? JSON.stringify(query.filter) : ''}&sort=${query.sort}&limit=${query.limit}&skip=${query.offset}`,
//     method: 'GET',
//   }));
// }

// export function transactionChequeCollectionGetList(query : any) {
//   return directusClient.request(() => ({
//     path: `/transaction/cheque/collection?filter=${helper.isNotNullAndUndefined(query.filter) ? JSON.stringify(query.filter) : ''}&sort=${query.sort}&limit=${query.limit}&skip=${query.offset}`,
//     method: 'GET',
//   }));
// }

// export function transactionCashCollectionGetList(query : any) {
//   return directusClient.request(() => ({
//     path: `/transaction/cash/collection?filter=${helper.isNotNullAndUndefined(query.filter) ? JSON.stringify(query.filter) : ''}&sort=${query.sort}&limit=${query.limit}&skip=${query.offset}`,
//     method: 'GET',
//   }));
// }

// export function accountGetAllList() {
//   const query = {
//     fields: ['id', 'code', 'name', 'account_type', 'summary_account', 'translations.name', 'translations.languages_code', 'sort'],
//     filter: {"status": { "_eq": 'published' }},
//     sort: ['code', 'sort'],
//     limit: -1
//   };

//   return directusClient.request(readItems('account', query));
// }

// export function chequeAccountGetAllList() {
//   const query = {
//     sort: ['name'],
//     limit: -1
//   };

//   return directusClient.request(readItems('cheque_account', query));
// }

// export function transactionCatGetAllList() {
//   const query = {
//     fields: ['id', 'code', 'name', 'receipt_text', 'account', 'tax', 'group', 'combine_type', 'translations.name', 'translations.languages_code'],
//     filter: {"status": { "_eq": 'published' }},
//     limit: -1
//   };

//   return directusClient.request(readItems('transaction_category', query));
// }

// export function transactionCatSet(data : any) {
//   return directusClient.request(() => ({
//     path: `/system/transcategory`,
//     body: JSON.stringify(data),
//     method: 'POST',
//   }));
// }

// export function transactionTypeGetAllList() {
//   const query = {
//     fields: ['id', 'name', 'entitlement', 'price', 'category', 'account', 'tax', 'translations.name', 'translations.languages_code', 'tablet', 'maxnum', 'printout', 'share', 'sort', 'action'],
//     filter: {"status": { "_eq": 'published' }},
//     limit: -1
//   };

//   return directusClient.request(readItems('transaction_type', query));
// }

// export function transactionCompanyGetAllList() {
//   const query = {
//     sort: ['company_number'],
//     limit: -1
//   };

//   return directusClient.request(readItems('transaction_company', query));
// }

// export function transactionTypeSet(data : any) {
//   return directusClient.request(() => ({
//     path: `/system/transtype`,
//     body: JSON.stringify(data),
//     method: 'POST',
//   }));
// }

// export function transactionCartGetAllList(cashierId : string) {
//   const query = {
//     fields: ['id', 'sort', 'user_created', 'date_created', 'user_updated', 'date_updated', 
//     'customer.id', 'customer.english_name', 'customer.chinese_name', 'customer.is_company', 'customer.avatar', 'customer.title', 
//     'customer.gender', 'customer.devotee_serial', 'customer.member_serial', 'customer.member_type',
//     'category', 'type', 'description', 'qty', 'unit_price', 'item_amount', 'tax', 'payable_amount', 'param', 'control_param'],
//     filter: {
//       "user_created": { "_eq": cashierId }
//     },
//     sort: ['sort'],
//     limit: -1
//   };

//   return directusClient.request(readItems('transaction_cart', query));
// }

// export function transactionCartSet(data : any) {
//   let id = data.id;
//   let cartItem = helper.deepCopy(data);
//   // Remove the fields that not allow to change.
//   delete cartItem.id;
  
//   if(!helper.stringHasValue(data.id))
//     return directusClient.request(createItem('transaction_cart', cartItem));
//   else
//     return directusClient.request(updateItem('transaction_cart', id, cartItem));
// }

// export function transactionCartSetMultiple(data : any) {
//   let ids = [];
//   let cartItems = helper.deepCopy(data);
  
//   if(!helper.arrayHasItem(cartItems)) {
//     return Promise.reject('Data cannot be empty.');
//   }

//   let updateMode = helper.stringHasValue(cartItems[0].id);

//   if(updateMode) {
//     // Populate the IDs for update
//     ids = cartItems.map(item => item.id);

//     // Remove the id from each of the cartItems object
//     cartItems.forEach(item => delete item.id);
//   }
  
//   if(!updateMode)
//     return directusClient.request(createItems('transaction_cart', cartItems));
//   else
//     return directusClient.request(updateItems('transaction_cart', ids, cartItems));
// }

// export function transactionCartItemDelete(itemId : string) {
//    return directusClient.request(deleteItem('transaction_cart', itemId));
// }

// export function transactionCartDelete(custId : string) {
//   return directusClient.request(deleteItems('transaction_cart', {
//     filter: {
//       "customer": { "_eq": custId }
//     }
//   }));
// }

export function userSettingGetAllList(userId : string) {
  const query = {
    // fields: ['id', 'name', 'entitlement', 'price', 'category', 'account', 'tax', 'translations.name', 'translations.languages_code', 'tablet', 'maxnum', 'printout', 'share'],
    filter: {
      "user_created": { "_eq": userId }
    },
    limit: -1
  };

  return directusClient.request(readItems('user_setting', query));
}

export function userSettingSet(data : any) {
  let id = data.id;
  let userSetting = helper.deepCopy(data);
  // Remove the fields that not allow to change.
  delete userSetting.id;
  
  if(!helper.stringHasValue(data.id))
    return directusClient.request(createItem('user_setting', userSetting));
  else
    return directusClient.request(updateItem('user_setting', id, userSetting));
}

// export function transactionCartPaymentGetAllList(custId : string) {
//   const query = {
//     filter: {
//       "customer": { "_eq": custId }
//     },
//     sort: 'sort',
//     limit: -1
//   };

//   return directusClient.request(readItems('transaction_cart_payment', query));
// }

// export function transactionCartPaymentSet(data : any) {
//   let id = data.id;
//   let cartItemPayment = helper.deepCopy(data);
//   // Remove the fields that not allow to change.
//   delete cartItemPayment.id;
  
//   if(!helper.stringHasValue(data.id))
//     return directusClient.request(createItem('transaction_cart_payment', cartItemPayment));
//   else
//     return directusClient.request(updateItem('transaction_cart_payment', id, cartItemPayment));
// }

// export function transactionCartPaymentDelete(paymentId : string) {
//    return directusClient.request(deleteItem('transaction_cart_payment', paymentId));
// }

// export function transactionCartPaymentDeleteByCustomer(custId : string) {
//   return directusClient.request(deleteItems('transaction_cart_payment', {
//     filter: {
//       "customer": { "_eq": custId }
//     }
//   }));
// }

// export function transactionProcessPayment(custId : string) {
//   return directusClient.request(() => ({
//     path: `/transaction/cart/${custId}`,
//     method: 'POST',
//   }));
// }

// export function transactionDonationBoxEntry(data : any) {
//   let now = new Date();
//   return directusClient.request(() => ({
//     path: `/transaction/donationbox/trans?tz=${now.getTimezoneOffset()}`,
//     method: 'POST',
//     body: JSON.stringify(data)
//   }));
// }

// export function transactionDonationBoxDelete(id : string ) {
//   let now = new Date();
//   return directusClient.request(() => ({
//     path: `/transaction/donationbox/trans/${id}?tz=${now.getTimezoneOffset()}`,
//     method: 'DELETE'
//   }));
// }

// export function transactionCounterReceiptUpdate(data : any ) {
//   return directusClient.request(() => ({
//     path: `/transaction/counterreceipt/trans`,
//     body: JSON.stringify(data),
//     method: 'PATCH'
//   }));
// }

// export function transactionCounterReceiptDelete(id : string ) {
//   let now = new Date();
//   return directusClient.request(() => ({
//     path: `/transaction/counterreceipt/trans/${id}?tz=${now.getTimezoneOffset()}`,
//     method: 'DELETE'
//   }));
// }

// export function transactionReceiptGet(transId : string) {
//   return directusClient.request(() => ({
//     path: `/transaction/${transId}`,
//     method: 'GET',
//   }));
// }

// export function transactionReceiptSet(data : any) {
//   let id = data.id;
//   let transaction = helper.deepCopy(data);
//   // Remove the fields that not allow to change.
//   delete transaction.id;
  
//   return directusClient.request(updateItem('transaction', id, transaction));
// }

// export function transactionOutstandingGet(catId : string, custId : string) {
//   return directusClient.request(() => ({
//     path: `/transaction/outstanding/${catId}/${custId}`,
//     method: 'GET',
//   }));
// }

// export function transactionCatGetLunarCalendar(catId : string) {
//   const query = {
//     fields: ['id', 'date', 'lunar_date', 'category'],
//     filter: {
//       "status": { "_eq": 'published' },
//       "category": { "_eq": catId }
//     },
//     sort: ['date'],
//     limit: -1
//   };

//   return directusClient.request(readItems('calendar_lunar', query));
// }

// export function lastEntryNamesGetList(ownerId : string, catId : string, typeId : string) {
//   return directusClient.request(() => ({
//     path: `/entry/lastentrynames/${ownerId}/${catId}/${typeId}`,
//     method: 'GET',
//   }));
// }

// export function relationshipGetList() {
//   const query = {
//     fields: ['id', 'sort', 'name', 'label', 'type'],
//     sort: ['sort'],
//     limit: -1
//   };

//   return directusClient.request(readItems('relationship', query));
// }

// export function descendentGetList() {
//   return directusClient.request(() => ({
//     path: `/entry/descendent`,
//     method: 'GET',
//   }));
// }

// export function adoptionQueryAvailItems(catId : string, keyword : string) {
//   const query = helper.stringHasValue(keyword) ? {
//     fields: ['id', 'itemcode', 'name', 'model', 'location', 'price'],
//     filter: {
//       "status": { "_eq": 'avail' },
//       "category": { "_eq": catId },
//       "customer": { "_null": true },
//       "_or": [
//         { "itemcode": { "_icontains": keyword }},
//         { "name": { "_icontains": keyword }}
//       ]
//     },
//     sort: ['itemcode'],
//     limit: -1
//   }
//   :
//   { // If no keyword, just return 20 entry under the selected catId
//     fields: ['id', 'itemcode', 'name', 'model', 'location', 'price'],
//     filter: {
//       "status": { "_eq": 'avail' },
//       "category": { "_eq": catId },
//       "customer": { "_null": true }
//     },
//     sort: ['itemcode'],
//     limit: 20
//   };

//   return directusClient.request(readItems('adoption_item', query));
// }

// export function printProfileList() {
//   const query = {
//     // fields: ['id', 'itemcode', 'name', 'model', 'location', 'price'],
//     filter: {
//       "status": { "_eq": 'published' },
//     },
//     sort: ['identifier'],
//     limit: -1
//   }
  
//   return directusClient.request(readItems('print_profile', query));
// }

// export function queryTabletForPrinting(queryParam : any ) {
//   // const query = {
//   //   fields: ['id', 'sort', 'transaction.id', 'transaction.trans_number', 'transaction.entry_date', 'category',
//   //            'type.id', 'serial', 'item_code', 'effective_year', 'label1', 'label2', 'label3', 'label4', 'prefix',
//   //            'lunar_date', 'date', 'family', 'deceased', 'relationship.id', 'relationship.name', 'relationship.label',
//   //            'relationship.type', 'descendent.id', 'descendent.name', 'descendent.fuyin', 'descendent.translations.id', 
//   //            'descendent.translations.languages_code', 'descendent.translations.name', 'display', 'company', 'program_type',
//   //            'time', 'remark', 'address', 'course.id', 'course.name'],
//   //   filter: queryParam,
//   //   // sort: ['identifier'],
//   //   limit: -1
//   // }
  
//   // return directusClient.request(readItems('transaction_displaydetail', query));

//   return directusClient.request(() => ({
//     path: `/printer/paiwei`,
//     method: 'POST',
//     body: JSON.stringify(queryParam)
//   }));
// }

export function reportGetAllList() {
  const query = {
    fields: ['id', 'sort', 'name', 'description', 'component', 'is_public', 'config', 'role.id', 'role.directus_roles_id', 'date_effective', 'date_expired', 'translations.name', 'translations.description', 'translations.languages_code'],
    filter: {"status": { "_eq": 'published' }},
    sort: ['sort'],
    limit: -1
  };

  return directusClient.request(readItems('report', query));
}

export function appCodeGetByType(type : string) {
  const query = {
    fields: ['id', 'sort', 'application', 'category', 'code', 'text', 'translations.text', 'translations.languages_code'],
    filter: {
      "status": { "_eq": 'published' },
      "category": { "_eq": type }
    },
    sort: ['sort'],
    limit: -1
  };

  return directusClient.request(readItems('app_code', query));
}

export function appContentGet(category : string, lang : string) {
  return directusClient.request(() => ({
    path: `/system/appcontent?category=${category}&lang=${lang}`,
    method: 'GET'
  }));
}

// export function reportSettlement(param : any ) {
//   return directusClient.request(() => ({
//     path: `/report/settlement`,
//     method: 'POST',
//     body: JSON.stringify(param)
//   }));
// }

// export function reportOfferingType(param : any ) {
//   return directusClient.request(() => ({
//     path: `/report/offeringtype`,
//     method: 'POST',
//     body: JSON.stringify(param)
//   }));
// }

// export function reportRevenue(param : any ) {
//   return directusClient.request(() => ({
//     path: `/report/revenue`,
//     method: 'POST',
//     body: JSON.stringify(param)
//   }));
// }

// export function reportDevoteeLetters(param : any ) {
//   return directusClient.request(() => ({
//     path: `/report/devoteeletters`,
//     method: 'POST',
//     body: JSON.stringify(param)
//   }));
// }

// export function reportDedicatedNames(param : any ) {
//   return directusClient.request(() => ({
//     path: `/report/dedicatednames`,
//     method: 'POST',
//     body: JSON.stringify(param)
//   }));
// }

// export function reportCeremonyStatus(param : any ) {
//   return directusClient.request(() => ({
//     path: `/report/ceremonystatus`,
//     method: 'POST',
//     body: JSON.stringify(param)
//   }));
// }

// export function reportDonationBoxList(param : any ) {
//   return directusClient.request(() => ({
//     path: `/report/donationboxlist`,
//     method: 'POST',
//     body: JSON.stringify(param)
//   }));
// }

// export function reportSupplierBillList(param : any ) {
//   return directusClient.request(() => ({
//     path: `/report/supplierbilllist`,
//     method: 'POST',
//     body: JSON.stringify(param)
//   }));
// }

// export function reportPaymentVoucherList(param : any ) {
//   return directusClient.request(() => ({
//     path: `/report/paymentvoucherlist`,
//     method: 'POST',
//     body: JSON.stringify(param)
//   }));
// }

// export function reportPettyCashList(param : any ) {
//   return directusClient.request(() => ({
//     path: `/report/pettycashlist`,
//     method: 'POST',
//     body: JSON.stringify(param)
//   }));
// }

// export function reimbursePettyCash(param : any ) {
//   return directusClient.request(() => ({
//     path: `/transaction/reimburse/pettycash`,
//     method: 'POST',
//     body: JSON.stringify(param)
//   }));
// }

// export function reportJournalVoucherList(param : any ) {
//   return directusClient.request(() => ({
//     path: `/report/journalvoucherlist`,
//     method: 'POST',
//     body: JSON.stringify(param)
//   }));
// }

// export function reportActivityList(param : any ) {
//   return directusClient.request(() => ({
//     path: `/report/activitylist`,
//     method: 'POST',
//     body: JSON.stringify(param)
//   }));
// }

// export function reportTrialBalance(param : any ) {
//   return directusClient.request(() => ({
//     path: `/report/trialbalance`,
//     method: 'POST',
//     body: JSON.stringify(param)
//   }));
// }

// export function transactionCatGetList(query : any) {
//   // console.log('Api customerGetList', query);
//   return Promise.all([
//     directusClient.request(aggregate('transaction_category', {
//       query: {
//         filter: query.filter
//       },
//       aggregate: {
//         count: '*',
//       }
//     })),
//     directusClient.request(readItems('transaction_category', query))
//   ]);
// }

// export function transactionTypeGetList(query : any) {
//   // console.log('Api customerGetList', query);
//   return Promise.all([
//     directusClient.request(aggregate('transaction_type', {
//       query: {
//         filter: query.filter
//       },
//       aggregate: {
//         count: '*',
//       }
//     })),
//     directusClient.request(readItems('transaction_type', query))
//   ]);
// }

// export function runningNumberGetAllList() {
//   const query = {
//     fields: ['id', 'name', 'source', 'type', 'last_sequent', 'template', 'min_length', 'translations.name', 'translations.languages_code'],
//     sort: ['name'],
//     limit: -1
//   };

//   return directusClient.request(readItems('running_number', query));
// }

// export function reportMailingList(param : any ) {
//   return directusClient.request(() => ({
//     path: `/report/mailinglist`,
//     method: 'POST',
//     body: JSON.stringify(param)
//   }));
// }

export function itemMgtGetList(entity : string, query : any) {
  // console.log('Api customerGetList', query);
  return Promise.all([
    directusClient.request(aggregate(entity, {
      query: {
        filter: query.filter
      },
      aggregate: {
        count: '*',
      }
    })),
    directusClient.request(readItems(entity, query))
  ]);
}

export function entityGetList(entity : string, query : any) {
  return directusClient.request(readItems(entity, query));
}

export function entitySet(entity : string, data : any) {
  
  delete data.user_created;
  delete data.date_created;
  delete data.user_updated;
  delete data.date_updated;

  if(!helper.stringHasValue(data.id))
    return directusClient.request(createItem(entity, data));
  else
    return directusClient.request(updateItem(entity, data.id, data));
}

export function entityCreateMultiple(entity : string, data : any[]) {
  return directusClient.request(createItems(entity, data));
}

export function entityTranslationSet(entity : string, data : any, lang : string, fields : {source : string, dest : string}[]) {
  let transId = 0;
  const newData : any = {
    languages_code: lang,
  }

  // console.log('entityTranslationSet: ', {entity, data, lang, fields});

  if(!helper.isNotNullAndUndefined(data) || !helper.arrayHasItem(fields) || !helper.stringHasValue(lang) || !helper.stringHasValue(entity)) {
    Promise.reject('Invalid paramters detected.');
  }

  // Get the translation id
  if(helper.arrayHasItem(data.translations)) {
    let transItem = data.translations.find((item : any) => item.languages_code === lang);
    if(helper.isNotNullAndUndefined(transItem?.id)) {
      transId = transItem.id;
    }

    fields.forEach(field => {
      newData[field.dest] = helper.carefullyGetStringValue(data, field.source.split('.'));
    });
  }

  // console.log('entityTranslationSet newData: ', {transId, newData});

  // If it is a new record, include the app_code_id field
  if(transId <= 0) {
    newData[`${entity}_id`] = data.id;
  }
  
  // console.log('codeTranslationSet: ', newData);
  if(transId <= 0)
    return directusClient.request(createItem(`${entity}_translations`, newData));
  else
    return directusClient.request(updateItem(`${entity}_translations`, transId, newData));
}

export function entityDelete(entity : string, id : string) {
  return directusClient.request(deleteItem(entity, id));
}

export function myProperty(userID : string) {
  const query = {
    // fields: [],
    filter: {
      "owner": { "_eq": userID },
      "status": { "_eq": "active" },
    },
    sort: ['-date_created'],
    limit: -1
  };

  return directusClient.request(readItems('property', query));
}

export function landlordLease(userID : string) {
  const query = {
    fields: ['*', 'tenant.*'],
    filter: {
      "property": {
        "owner": { "_eq": userID }
      },
      "status": { "_neq": "void" },
    },
    sort: ['-date_created'],
    limit: -1
  };

  return directusClient.request(readItems('lease', query));
}

export function leaseDetail(leaseID : string) {
  const query = {
    fields: ['*', 'tenant.*', 'property.*', 'payment.*', 'subscription_plan.*']
  };

  return directusClient.request(readItem('lease', leaseID, query));
}

export async function subscriptionPlanGetList(lang : string) {
  const query = {
    fields: ['id', 'sort', 'status', 'application', 'type', 'name', 'description', 'price', 'date_effective', 'date_expired', 'param', 'translations.name', 'translations.description', 'translations.languages_code'],
    filter: {"status": { "_eq": 'published' }},
    sort: ['sort'],
    limit: -1
  };

  const data = await directusClient.request(readItems('subscription_plan', query));

  // Perform multi lang field replacement
  data.forEach(item => {
    helper.replaceEntityFieldWithLang(lang, item, ['name', 'description']);
  });

  return data;
}

export function makePayment(data : any) {
  return directusClient.request(() => ({
    path: `/payment`,
    body: JSON.stringify(data),
    method: 'POST',
  }));
}

export function propertyLease(data : any) {
  return directusClient.request(() => ({
    path: `/property`,
    body: JSON.stringify(data),
    method: 'POST',
  }));
}

export function updatePropertyLease(data : any) {
  return directusClient.request(() => ({
    path: `/property`,
    body: JSON.stringify(data),
    method: 'PATCH',
  }));
}

export function deletePropertyLease(data : any) {
  return directusClient.request(() => ({
    path: `/property`,
    body: JSON.stringify(data),
    method: 'DELETE',
  }));
}

// Require admin right
// export function runningNumberSet(param : any ) {
//   return directusClient.request(() => ({
//     path: `/system/serialreg`,
//     method: 'POST',
//     body: JSON.stringify(param)
//   }));
// }

// Non admin right update
// export function runningNumberUpdate(param : any ) {
//   return directusClient.request(() => ({
//     path: `/system/serialreg`,
//     method: 'PATCH',
//     body: JSON.stringify(param)
//   }));
// }

// export function runningNumberDelete(id : string ) {
//   return directusClient.request(() => ({
//     path: `/system/serialreg/${id}`,
//     method: 'DELETE'
//   }));
// }

// export function currencyGetAllList() {
//   const query = {
//     sort: ['code'],
//     limit: -1
//   };

//   return directusClient.request(readItems('currency', query));
// }

// export function journalVoucherGet(id : string ) {
//   return directusClient.request(() => ({
//     path: `/finance/journalvoucher/${id}`,
//     method: 'GET'
//   }));
// }

// export function journalVoucherSet(data : any) {
//   return directusClient.request(() => ({
//     path: `/finance/journalvoucher`,
//     body: JSON.stringify(data),
//     method: 'POST',
//   }));
// }

// export function journalVoucherDelete(id : string) {
//   return directusClient.request(() => ({
//     path: `/finance/journalvoucher/${id}`,
//     method: 'DELETE',
//   }));
// }

// export function journalVoucherDup(id : string) {
//   return directusClient.request(() => ({
//     path: `/finance/journalvoucher/duplicate/${id}`,
//     body: JSON.stringify({}),
//     method: 'POST',
//   }));
// }

// export function journalVoucherPost(data : any) {
//   return directusClient.request(() => ({
//     path: `/finance/journalpost`,
//     body: JSON.stringify(data),
//     method: 'POST',
//   }));
// }

// export function journalVoucherUnpost(data : any) {
//   return directusClient.request(() => ({
//     path: `/finance/journalunpost`,
//     body: JSON.stringify(data),
//     method: 'POST',
//   }));
// }

// export function chequeGet(id : string ) {
//   return directusClient.request(() => ({
//     path: `/finance/chequedetail/${id}`,
//     method: 'GET'
//   }));
// }

// export function chequeCollectionDetail(id : string ) {
//   return directusClient.request(() => ({
//     path: `/transaction/cheque/collection/${id}`,
//     method: 'GET'
//   }));
// }

// export function chequeCollectionNew(data : any) {
//   return directusClient.request(() => ({
//     path: `/transaction/cheque/collection`,
//     body: JSON.stringify(data),
//     method: 'POST',
//   }));
// }

// export function chequeCollectionUpdate(data : any) {
//   return directusClient.request(() => ({
//     path: `/transaction/cheque/collection`,
//     body: JSON.stringify(data),
//     method: 'PATCH',
//   }));
// }

// export function chequeCollectionCashIn(data : any) {
//   return directusClient.request(() => ({
//     path: `/transaction/cheque/cashin`,
//     body: JSON.stringify(data),
//     method: 'PATCH',
//   }));
// }

// export function chequeCollectionDelete(id : string ) {
//   return directusClient.request(() => ({
//     path: `/transaction/cheque/collection/${id}`,
//     method: 'DELETE'
//   }));
// }

// export function cashCollectionDetail(id : string ) {
//   return directusClient.request(() => ({
//     path: `/transaction/cash/collection/${id}`,
//     method: 'GET'
//   }));
// }

// export function cashCollectionNew(data : any) {
//   return directusClient.request(() => ({
//     path: `/transaction/cash/collection`,
//     body: JSON.stringify(data),
//     method: 'POST',
//   }));
// }

// export function cashCollectionUpdate(data : any) {
//   return directusClient.request(() => ({
//     path: `/transaction/cash/collection`,
//     body: JSON.stringify(data),
//     method: 'PATCH',
//   }));
// }

// export function cashCollectionDelete(id : string ) {
//   return directusClient.request(() => ({
//     path: `/transaction/cash/collection/${id}`,
//     method: 'DELETE'
//   }));
// }

// export function supplierBillDetail(id : string ) {
//   return directusClient.request(() => ({
//     path: `/transaction/payment/supplierbill/${id}`,
//     method: 'GET'
//   }));
// }

// export function supplierBillGetByNumber(billNumber : string) {
//   const query = {
//     // fields: ['id', 'status', 'sort', 'name', 'tooltip', 'route', 'param', 'icon', 'is_container',
//     // 'is_public', 'parent_menu', 'date_effective', 'date_expired', 'role.id', 'role.directus_roles_id',
//     // 'translations.id', 'translations.languages_code', 'translations.name', 'translations.tooltip'],
//     filter: {"invoice_number": { "_eq": billNumber }},
//     limit: -1
//   };

//   return directusClient.request(readItems('transaction_bill', query));
// }

// export function supplierBillNew(data : any) {
//   return directusClient.request(() => ({
//     path: `/transaction/payment/supplierbill`,
//     body: JSON.stringify(data),
//     method: 'POST',
//   }));
// }

// export function supplierBillUpdate(data : any) {
//   return directusClient.request(() => ({
//     path: `/transaction/payment/supplierbill`,
//     body: JSON.stringify(data),
//     method: 'PATCH',
//   }));
// }

// export function supplierBillMakePayment(data : any) {
//   return directusClient.request(() => ({
//     path: `/transaction/payment/makebillpayment`,
//     body: JSON.stringify(data),
//     method: 'POST',
//   }));
// }

// export function supplierBillDeletePayment(id : string) {
//   return directusClient.request(() => ({
//     path: `/transaction/payment/deletebillpayment/${id}`,
//     method: 'DELETE',
//   }));
// }

// export function supplierBillMakeMultiBillPayment(data : any) {
//   return directusClient.request(() => ({
//     path: `/transaction/payment/makemultibillpayment`,
//     body: JSON.stringify(data),
//     method: 'POST',
//   }));
// }

// export function supplierBillDelete(id : string ) {
//   return directusClient.request(() => ({
//     path: `/transaction/payment/supplierbill/${id}`,
//     method: 'DELETE'
//   }));
// }

// export function supplierBillGetOutstanding(data : any) {
//   return directusClient.request(() => ({
//     path: `/transaction/payment/supplierbill/outstanding`,
//     body: JSON.stringify(data),
//     method: 'POST',
//   }));
// }

// export function chequePaymentDetail(id : string ) {
//   return directusClient.request(() => ({
//     path: `/transaction/payment/cheque/${id}`,
//     method: 'GET'
//   }));
// }

// export function chequePaymentNew(data : any) {
//   return directusClient.request(() => ({
//     path: `/transaction/payment/cheque`,
//     body: JSON.stringify(data),
//     method: 'POST',
//   }));
// }

// export function chequePaymentUpdate(data : any) {
//   return directusClient.request(() => ({
//     path: `/transaction/payment/cheque`,
//     body: JSON.stringify(data),
//     method: 'PATCH',
//   }));
// }

// export function chequePaymentVoid(id : string) {
//   return directusClient.request(() => ({
//     path: `/transaction/payment/chequevoid/${id}`,
//     method: 'PATCH',
//   }));
// }

// export function chequePaymentDelete(id : string ) {
//   return directusClient.request(() => ({
//     path: `/transaction/payment/cheque/${id}`,
//     method: 'DELETE'
//   }));
// }

// export function cashPaymentGetList(query : any) {
//   return directusClient.request(() => ({
//     path: `/transaction/payment/cash?filter=${helper.isNotNullAndUndefined(query.filter) ? JSON.stringify(query.filter) : ''}&sort=${query.sort}&limit=${query.limit}&skip=${query.offset}`,
//     method: 'GET',
//   }));
// }

// export function cashPaymentGetDetail(id : string ) {
//   return directusClient.request(() => ({
//     path: `/transaction/payment/cash/${id}`,
//     method: 'GET'
//   }));
// }

// export function cashPaymentNew(data : any) {
//   return directusClient.request(() => ({
//     path: `/transaction/payment/cash`,
//     body: JSON.stringify(data),
//     method: 'POST',
//   }));
// }

// export function cashPaymentUpdate(data : any) {
//   return directusClient.request(() => ({
//     path: `/transaction/payment/cash`,
//     body: JSON.stringify(data),
//     method: 'PATCH',
//   }));
// }

// export function cashPaymentDelete(id : string ) {
//   return directusClient.request(() => ({
//     path: `/transaction/payment/cash/${id}`,
//     method: 'DELETE'
//   }));
// }

// export function pettyCashPaymentGetList(query : any) {
//   return directusClient.request(() => ({
//     path: `/transaction/payment/pettycash?filter=${helper.isNotNullAndUndefined(query.filter) ? JSON.stringify(query.filter) : ''}&sort=${query.sort}&limit=${query.limit}&skip=${query.offset}`,
//     method: 'GET',
//   }));
// }

// export function pettyCashPaymentGetDetail(id : string ) {
//   return directusClient.request(() => ({
//     path: `/transaction/payment/pettycash/${id}`,
//     method: 'GET'
//   }));
// }

// export function pettyCashPaymentNew(data : any) {
//   return directusClient.request(() => ({
//     path: `/transaction/payment/pettycash`,
//     body: JSON.stringify(data),
//     method: 'POST',
//   }));
// }

// export function pettyCashPaymentUpdate(data : any) {
//   return directusClient.request(() => ({
//     path: `/transaction/payment/pettycash`,
//     body: JSON.stringify(data),
//     method: 'PATCH',
//   }));
// }

// export function pettyCashPaymentDelete(id : string ) {
//   return directusClient.request(() => ({
//     path: `/transaction/payment/pettycash/${id}`,
//     method: 'DELETE'
//   }));
// }